import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import { API_URL, axios } from "../constants";
import {
  getCustomerToken, setCustomerToken, clearCustomerToken,
  getCustomerAuthHeaders, isCustomerLoggedIn,
} from "../utils/customerAuth";
import AuthGateModal from "../components/auth/AuthGateModal";

// Strip "www." so Emergent auth shows "DoozyGo" not "Www" as the app name.
const getNormalizedOrigin = () => window.location.origin.replace("://www.", "://");
// Emergent Google OAuth must always return to the existing /my-account exchange route.
export const getGoogleAuthUrl = () =>
  `https://auth.emergentagent.com/?redirect=${encodeURIComponent(getNormalizedOrigin() + "/my-account")}`;

const CustomerAuthContext = createContext(null);

export function CustomerAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const pendingRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!isCustomerLoggedIn()) { setUser(null); setChecking(false); return null; }
    try {
      const res = await axios.get(`${API_URL}/api/customer-auth/me`, {
        withCredentials: true, headers: getCustomerAuthHeaders(),
      });
      setUser(res.data);
      return res.data;
    } catch (e) {
      if (e.response?.status === 401) { clearCustomerToken(); setUser(null); }
      return null;
    } finally { setChecking(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Called by the modal after a successful phone-OTP login (no navigation).
  const onLoginSuccess = useCallback(async (sessionToken) => {
    if (sessionToken) setCustomerToken(sessionToken);
    const me = await refresh();
    setModalOpen(false);
    const pending = pendingRef.current;
    pendingRef.current = null;
    if (pending && me) { try { await pending(); } catch { /* action handles its own errors */ } }
  }, [refresh]);

  // Google uses a full-page redirect; carry the originating page so /my-account
  // can send the user back (reuses the existing doozygo_return_url mechanism).
  const startGoogle = useCallback(() => {
    try {
      localStorage.setItem("doozygo_return_url", window.location.pathname + window.location.hash);
    } catch { /* storage disabled — user simply lands on /my-account */ }
    window.location.href = getGoogleAuthUrl();
  }, []);

  // The one entry point every gated feature calls. Runs `action` immediately if
  // logged in, else opens the login modal and resumes `action` after login.
  const requireAuth = useCallback((action) => {
    if (isCustomerLoggedIn()) { return Promise.resolve(action ? action() : true); }
    pendingRef.current = action || null;
    setModalOpen(true);
    return Promise.resolve(false);
  }, []);

  const closeModal = useCallback(() => { pendingRef.current = null; setModalOpen(false); }, []);

  const logout = useCallback(async () => {
    try { await axios.post(`${API_URL}/api/customer-auth/logout`, {}, { withCredentials: true, headers: getCustomerAuthHeaders() }); } catch { /* ignore */ }
    clearCustomerToken();
    setUser(null);
  }, []);

  const value = {
    user, loggedIn: !!user, checking,
    requireAuth, refresh, logout,
    openLogin: () => requireAuth(null),
  };

  return (
    <CustomerAuthContext.Provider value={value}>
      {children}
      <AuthGateModal
        open={modalOpen}
        onClose={closeModal}
        onLoginSuccess={onLoginSuccess}
        startGoogle={startGoogle}
      />
    </CustomerAuthContext.Provider>
  );
}

export function useCustomerAuth() {
  const ctx = useContext(CustomerAuthContext);
  if (!ctx) throw new Error("useCustomerAuth must be used within CustomerAuthProvider");
  return ctx;
}
